// js/_shared/_utils.js
/* =============================================================================
   SHARED UTILITIES
   Path: /js/_shared/_utils.js
   
   Purpose: Generic DOM helpers and utility functions used across the application.
            No view-specific logic. Foundation for all other modules.
   
   Dependencies: None (load first)
   
   Namespace: window.AppUtils
   Global Aliases: window.qs, window.qsa (for convenience)
   ========================================================================== */

window.AppUtils = (function () {
    'use strict';

    /* =========================================================================
       DOM QUERY HELPERS
       ========================================================================= */

    /**
     * querySelector shorthand
     * @param {string} sel - CSS selector
     * @param {Element} [root=document] - Parent element to search within
     * @returns {Element|null}
     */
    function qs(sel, root) {
        return (root || document).querySelector(sel);
    }

    /**
     * querySelectorAll shorthand - returns real array
     * @param {string} sel - CSS selector
     * @param {Element} [root=document] - Parent element to search within
     * @returns {Element[]}
     */
    function qsa(sel, root) {
        return Array.prototype.slice.call((root || document).querySelectorAll(sel));
    }

    /**
     * Check if element is visible (display, visibility, opacity)
     * @param {Element} el - Element to check
     * @returns {boolean}
     */
    function isVisible(el) {
        if (!el) return false;
        var style = getComputedStyle(el);
        return style.display !== 'none' &&
               style.visibility !== 'hidden' &&
               style.opacity !== '0';
    }

    /* =========================================================================
       FORM HELPERS
       ========================================================================= */

    /**
     * Get ASP.NET anti-forgery token
     * @param {Element} [scopeEl] - Form to search first (falls back to document)
     * @returns {string|null}
     */
    function getToken(scopeEl) {
        var input = null;
        if (scopeEl) {
            input = scopeEl.querySelector('input[name="__RequestVerificationToken"]');
        }
        if (!input) {
            input = document.querySelector('input[name="__RequestVerificationToken"]');
        }
        return input ? input.value : null;
    }

    /**
     * Basic email format validation
     * @param {string} email - Email to validate
     * @returns {boolean}
     */
    function isValidEmail(email) {
        if (!email) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    }

    /* =========================================================================
       TEXT & FOCUS HELPERS
       ========================================================================= */

    /**
     * Safely set textContent on an element
     * @param {Element} el - Target element
     * @param {string} text - Text to set
     */
    function setText(el, text) {
        if (el) {
            el.textContent = text;
        }
    }

    /**
     * Focus element if it exists and is visible
     * @param {string|Element} selOrEl - Selector string or Element
     */
    function focusIfPossible(selOrEl) {
        var el = typeof selOrEl === 'string' ? qs(selOrEl) : selOrEl;
        if (el && typeof el.focus === 'function' && isVisible(el)) {
            el.focus();
        }
    }

    /* =========================================================================
       PUBLIC API
       ========================================================================= */

    /**
     * Escape a string for safe insertion into HTML. Use this single shared boundary for any
     * server-/user-supplied value built into innerHTML, instead of the per-view escapeHtml copies -
     * it is the structural control for the recurring "untrusted free-text into an HTML sink" class.
     * @param {*} text
     * @returns {string}
     */
    function escapeHtml(text) {
        if (text == null) return '';
        return String(text)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    return {
        // DOM Query
        qs: qs,
        qsa: qsa,
        isVisible: isVisible,

        // Form Helpers
        getToken: getToken,
        isValidEmail: isValidEmail,

        // Text & Focus
        setText: setText,
        focusIfPossible: focusIfPossible,

        // HTML safety
        escapeHtml: escapeHtml
    };

})();

/* =============================================================================
   GLOBAL ALIASES
   These provide convenient shortcuts for frequently used functions
   ========================================================================== */

window.qs = AppUtils.qs;
window.qsa = AppUtils.qsa;
// Single shared HTML-escaping boundary for values built into innerHTML (use instead of per-view copies).
window.escapeHtml = AppUtils.escapeHtml;
